import React, { useState } from 'react'
import { makeApiCall } from '../utils/customerUtils'

interface PhoneLinkProps {
  phoneNumber: string
  customerId: number
  dealId?: number
  customerName?: string
  className?: string
  showIcon?: boolean
}

export const PhoneLink: React.FC<PhoneLinkProps> = ({
  phoneNumber,
  customerId,
  dealId,
  customerName,
  className = '',
  showIcon = true
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleCall = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (isLoading) return

    setIsLoading(true)
    setError(null)

    try {
      // Try to trigger the Telnyx call widget first (if available)
      const telnyxCallEvent = new CustomEvent('triggerTelnyxCall', {
        detail: { 
          phoneNumber: phoneNumber, 
          customerId: customerId, 
          customerName: customerName 
        }
      })
      window.dispatchEvent(telnyxCallEvent)

      // Fallback to API call if widget isn't available
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication token not found')
      }

      const requestBody: any = {
        phone_number: phoneNumber,
        customer_id: customerId
      }
      // Only include deal_id if it has a valid value
      if (dealId) {
        requestBody.deal_id = dealId
      }

      const response = await makeApiCall('api/calls/outbound', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to initiate call')
      }

      // Show success message
      alert(`Call initiated successfully to ${customerName || phoneNumber}. Call Control ID: ${data.call_control_id}`)

    } catch (error) {
      console.error('Error initiating call:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to initiate call'
      setError(errorMessage)
      alert(`Failed to initiate call: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }

  // Format phone number for display
  const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '')
    
    // Format as (XXX) XXX-XXXX for 10-digit US numbers
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
    }
    
    // For international numbers or other formats, return as-is
    return phone
  }

  const formattedPhone = formatPhoneNumber(phoneNumber)

  return (
    <button
      onClick={handleCall}
      disabled={isLoading}
      className={`inline-flex items-center space-x-1 text-gray-900 hover:text-blue-800 dark:text-gray-600 dark:hover:text-blue-300 transition-colors ${
        isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:underline'
      } ${className}`}
      title={`Call ${customerName || formattedPhone}`}
    >
      {showIcon && (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
          />
        </svg>
      )}
      <span className={isLoading ? 'text-gray-400' : ''}>
        {isLoading ? 'Calling...' : formattedPhone}
      </span>
    </button>
  )
}

// Alternative component for just showing a clickable phone icon
interface PhoneIconProps {
  phoneNumber: string
  customerId: number
  dealId?: number
  customerName?: string
  size?: number
  className?: string
}

export const PhoneIcon: React.FC<PhoneIconProps> = ({
  phoneNumber,
  customerId,
  dealId,
  customerName,
  size = 18,
  className = ''
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const handleCall = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (isLoading) return

    setIsLoading(true)

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication token not found')
      }

      const response = await makeApiCall('api/calls/outbound', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ phone_number: phoneNumber })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to initiate call')
      }

      // Show success message
      alert(`Call initiated successfully to ${customerName || phoneNumber}. Call SID: ${data.call_sid}`)

    } catch (error) {
      console.error('Error initiating call:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to initiate call'
      alert(`Failed to initiate call: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <button
      onClick={handleCall}
      disabled={isLoading}
      className={`inline-flex items-center justify-center p-1 rounded-full text-green-600 hover:text-green-800 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20 transition-colors ${
        isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${className}`}
      title={`Call ${customerName || phoneNumber}`}
    >
      <svg
        width={size}
        height={size}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        className={isLoading ? 'animate-pulse' : ''}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
        />
      </svg>
    </button>
  )
} 